<template>
    <b-container fluid>
        <b-alert :show="!!prescription?.hasBillingWarning" variant="danger" class="mb-2">
            <b-icon icon="exclamation-triangle-fill" aria-hidden="true"></b-icon>
            Billing warning: {{ prescription?.billingWarningReason }}
        </b-alert>
        <b-row id="header-row">
            <b-col>
                RxId: {{ prescription?.rxId }}
            </b-col>
            <b-col>
                <b-button variant="info" @click="fetchScriptHistory">Script History</b-button>
            </b-col>
        </b-row>
        <b-row id="script-details-row">
            <b-col id="image-column"
                   cols="6">
                <image-component :image-i-d="prescription?.imageID"
                                 :rx-number="prescription?.rxNumber"
                                 :escript-i-d="prescription?.escriptID"/>
            </b-col>
            <b-col id="detail-column">
                <b-row>
                    <b-col cols="3">
                        <b>Patient:</b>
                    </b-col>
                    <b-col>
                        <b-row>
                            <b-col>
                                {{ patient.displayNamesForPerson() }}
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                {{ dateFormatter(patient?.dateOfBirth?.toString()) }} - {{ patientAge }}
                            </b-col>
                        </b-row>
                        <hr/>
                        <b-row>
                            <b-col>
                                {{ prescription?.address1 }}
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                {{ prescription?.address2 }}
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                {{ prescription?.addressCity }}, {{ prescription?.addressState }}
                                {{ prescription?.addressZip }}
                            </b-col>
                        </b-row>
                        <hr/>
                        <b-row>
                            <b-col><b>Height:</b> {{ patient.height }}</b-col>
                            <b-col><b>Weight:</b> {{ patient.weight }}</b-col>
                        </b-row>
                    </b-col>
                </b-row>
                <hr/>
                <b-row>
                    <b-col cols="3">
                        <b>Prescriber:</b>
                    </b-col>
                    <b-col>
                        {{ prescription?.doctorLastName }}, {{ prescription?.doctorFirstName }}
                    </b-col>
                </b-row>
                <hr/>
                <b-row>
                    <b-col cols="3">
                        <b>Drug:</b>
                    </b-col>
                    <b-col>
                        <b-row>
                            <b-col>
                                NDC: {{ ndc }}
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                {{ prescription?.productNameShort }}
                            </b-col>
                        </b-row>

                    </b-col>

                </b-row>
                <hr/>
                <b-row>
                    <b-col cols="3">
                        <b>Fill Date:</b>
                    </b-col>
                    <b-col>
                        {{ dateFormatter(prescription?.fillDate.toString()) }}
                    </b-col>
                </b-row>
                <hr/>
                <b-row>
                    <b-col cols="3">
                        <b>Written Date:</b>
                    </b-col>
                    <b-col>
                        {{ dateFormatter(prescription?.writtenDate.toString()) }}
                    </b-col>
                </b-row>
                <hr/>
                <b-row>
                    <b-col cols="3">
                        <b>Order Date:</b>
                    </b-col>
                    <b-col>
                        {{ dateFormatter(prescription?.orderDate.toString()) }}
                    </b-col>
                </b-row>
                <hr/>
                <b-row>
                    <b-col cols="3">
                        <b>Sig:</b>
                    </b-col>
                    <b-col>
                        {{ prescription?.directions }}
                    </b-col>
                </b-row>
                <hr/>
                <b-row>
                    <b-col cols="3">
                        <b>Days Supply:</b>
                    </b-col>
                    <b-col>
                        {{ prescription?.daySupply }}
                    </b-col>
                </b-row>
                <hr/>
                <b-row>
                    <b-col cols="3">
                        <b>Refills:</b>
                    </b-col>
                    <b-col>
                        {{ prescription?.refills }}
                    </b-col>
                </b-row>
                <hr/>
                <b-row>
                    <b-col cols="3">
                        <b>DAW:</b>
                    </b-col>
                    <b-col>{{ prescription?.dawCode }}</b-col>
                </b-row>
                <hr/>
                <b-row>
                    <b-col cols="3"><b>Allergies:</b></b-col>
                    <b-col>
                        <b-row v-for="(allergy) in allergies" :key="allergy.allergyId">
                            <b-col>
                                {{ this.allergyString(allergy) }}
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
                <hr/>
                <b-row>
                    <b-col cols="3"><b>Diagnosis Codes:</b></b-col>
                    <b-col>
                        <b-row v-if="diagnoses.length == 0">
                            <b-col>
                                No Diagnosis Codes Recorded
                            </b-col>
                        </b-row>
                        <b-row v-else v-for="diagnosis in diagnoses" :key="diagnosis.id">
                            <b-col>
                                <b>{{ diagnosis.icD10Code }}</b> - {{ diagnosis.description }}
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
        <b-row id="script-history-row" v-show="scriptHistory?.length > 0" class="my-2">
            <b-col>
                <b-table :items="scriptHistory"
                         :fields="scriptHistoryFields"
                         caption-top
                         caption="Prescription History"
                         sort-icon-left
                         outlined/>
            </b-col>
        </b-row>
    </b-container>
</template>

<script lang="ts">
import {Component, VModel, Vue, Watch} from "vue-facing-decorator";
import ImageComponent from "@/components/ImageComponent.vue";
import {OrderLabelData} from "@/models/OrderLabelData";
import axios from "axios";
import {AllergyType, GSDDPatientAllergy, GSDDPatientDiagnosis} from "@/models/GSDD";
import {Patient} from "@/models/Patient";
import {BvTableFieldArray} from "bootstrap-vue";

@Component({
    components: {ImageComponent},
})
export default class PrescriptionDetails extends Vue {
    @VModel({name: 'value'}) private prescription!: OrderLabelData | null;
    protected allergies: GSDDPatientAllergy[] = [];
    protected scriptHistory: OrderLabelData[] = [];
    protected diagnoses: GSDDPatientDiagnosis[] = [];
    protected patient: Patient = new Patient();

    @Watch('prescription')
    prescriptionChange():void {
        this.allergies = [];
        this.diagnoses = [];
        this.scriptHistory = [];
        this.fetchAllergies();
    }

    mounted():void {
        this.fetchAllergies();
    }

    allergyString(allergy: GSDDPatientAllergy): string {
        if (!allergy) return "";
        return `${AllergyType[allergy.allergyType]}${allergy.allergyName.length > 0 ? ' - ' + allergy.allergyName : ''}`;
    }

    async fetchScriptHistory():Promise<void> {
        if (!this.prescription?.rxId) return;
        console.log("Fetch script history");
        try {
            let response = await axios.get<OrderLabelData[]>(`/api/DoctorQueue/History/${this.prescription?.rxId}`);
            this.scriptHistory = response.data;
        } catch (err) {
            console.log("err: ", err);
        }

    }

    async fetchAllergies(): Promise<void> {
        if (!this.prescription?.rxId) return;
        try {
            let response = await axios.get<{
                allergies: GSDDPatientAllergy[],
                diagnoses: GSDDPatientDiagnosis[],
                patient: Patient,
            }>(`/api/DoctorQueue/Details/${this.prescription?.rxId}`);
            this.diagnoses = response.data.diagnoses;
            this.allergies = response.data.allergies;
            this.patient = new Patient(response.data.patient);
            if (this.allergies.length === 0) {
                let noDocumentedAllergies: GSDDPatientAllergy = {
                    patientId: this.prescription.patientID,
                    allergyType: AllergyType["None Documented"],
                    allergyId: 0,
                    allergyName: "",
                };
                this.allergies = [noDocumentedAllergies];
            }
        } catch (err) {
            let errorAllergy: GSDDPatientAllergy = {
                allergyType: AllergyType.Error,
                allergyName: "An error occured when attempting to load known allergies",
                allergyId: 0,
                patientId: this.prescription.patientID,
            };
            this.allergies = [errorAllergy];
            console.log("err: ", err);
        }
    }

    get scriptHistoryFields(): BvTableFieldArray {
        return [
            {key: "fillDate", label: "Date", formatter: this.dateFormatter, sortable: true},
            {key: "patientID", label: "Patient", formatter: this.patientFormatter},
            {key: "ndC11", label: "NDC"},
            {key: "productNameShort", label: "Drug"},
            {key: "dispensed", label: "Dispensed"},
            {key: "storeName", label: "Store"},
            {key: "rxId", label: "Rx Id"},
        ];
    }

    get ndc(): string {
        if (!this.prescription?.ndc) return "";
        return `${this.prescription?.ndc.substr(0, 4)}-${this.prescription?.ndc.substr(4, 5)}-${this.prescription?.ndc.substr(9)}`;
    }

    get patientAge(): string {
        return `${this.patient.age} Years Old`;
    }

    patientFormatter(value: number, key: string, item: any): string {
        return `${item.patient.lastName}, ${item.patient.firstName}`;
    }

    dateFormatter(value: string): string {
        if (!value) return "";
        let date = Date.parse(value);
        return Intl.DateTimeFormat().format(date);
    }
}
</script>

<style scoped>

</style>
